import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, from } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { CandleStickService } from 'src/app/api/candle-stick.service';
import { rangePairs } from 'src/app/helpers/helpers';
import { CandleStickItem, CandleStickRequest } from 'src/app/models/candle-stick';
import { MaximumAllowedDurations, RealtimeCandleSubscription, Resolutions, TVBar } from 'src/app/models/trading-view';
import { ResolutionString } from 'src/assets/tradingview/charting_library/charting_library';
import { environment } from 'src/environments/environment';
import { CurrenciesService } from '../api/currencies.service';
import { WebsocketCandleStickService } from './websocket-candle-stick.service';

@Injectable({
  providedIn: 'root'
})
export default class CryptoCandleService implements OnDestroy {
  private _realtimeSubscriptions = new Map<string, RealtimeCandleSubscription>();
  private _lastCandles = new Map<string, TVBar>();

  constructor(
    private wssCandleStickService: WebsocketCandleStickService,
    private candleStickService: CandleStickService,
    private currenciesService: CurrenciesService) { }

  ngOnDestroy() {
    this._realtimeSubscriptions.forEach(subscription => this.unsubscribeLiveUpdates(subscription.uuid));
    this.wssCandleStickService.disconnect();
  }

  private _getSecondsFromResolution(resolutions: ResolutionString): number {
    switch (resolutions) {
      case Resolutions.M1:
      case Resolutions.M5:
      case Resolutions.M15:
      case Resolutions.M30:
      case Resolutions.M45:
      case Resolutions.M60:
      case Resolutions.M120:
      case Resolutions.M240:
      case Resolutions.M360:
      case Resolutions.M720:
        return parseInt(resolutions) * 60;
      case 'D':
      case Resolutions.D1:
        return 24 * 60 * 60;
      case 'W':
      case Resolutions.W1:
        return 7 * 24 * 60 * 60;
      default:
        console.error(
          'Invalid resolution parameter: ' + resolutions + '! Possible values are 1, 5, 15, 30, 45, 60, 120, 240, 360, 720, D and W.'
        );
        return 30 * 60;
    }
  }

  private _getBarDataFromResponse(response: CandleStickItem[]): TVBar[] {
    const data = response.sort((dataPointA, dataPointB) => parseInt(dataPointA?.t, 10) - parseInt(dataPointB?.t, 10));
    const bars = data.map(dataPoint => ({
      time: parseInt(dataPoint?.t, 10) * 1000,
      open: Number(dataPoint?.o),
      close: Number(dataPoint?.c),
      high: Number(dataPoint?.h),
      low: Number(dataPoint?.l),
    }));

    return bars;
  }

  private _getRateLimitedRequest(pair: string, start: number, end: number, resolution: ResolutionString): CandleStickRequest[] {
    const duration = end - start;
    const maxDuration = MaximumAllowedDurations[resolution];
    const isDurationExceeded = maxDuration && duration > maxDuration;
    const intervalInSeconds = this._getSecondsFromResolution(resolution);
    const defaultDuration = [start, end];
    const durationPairs = isDurationExceeded ? rangePairs(start, end, maxDuration) : [defaultDuration];

    return durationPairs.map(([startTime, endTime]) => ({
      startTime: startTime.toString(),
      endTime: endTime.toString(),
      inCurrency: environment.currencies.globalCurrency,
      pair,
      intervalInSeconds
    }));
  }

  private _getUnifiedCandlesHistory(pair, periodParams, resolution) {
    const candleRequests = this._getRateLimitedRequest(pair, periodParams.from, periodParams.to, resolution);
    const candles$ = forkJoin(candleRequests.map(request => this.candleStickService.getCandles(request)));
    const fromInMs = periodParams.from * 1000;
    const toInMs = periodParams.to * 1000;

    const barsInRange = ({ time }: TVBar) => time >= fromInMs && time <= toInMs;
    const unifyCandlesResponse = (previous, current) => [...current, ...previous];

    return candles$.pipe(
      map((candlesResponse) => {
        if (candlesResponse?.length === 0) {
          return [];
        }
        const response = candlesResponse.reduce(unifyCandlesResponse, []);
        const bars = this._getBarDataFromResponse(response);
        return bars.filter(barsInRange);
      })
    );
  }

  private _getFutureProofPeriodParams(periodParams) {
    const periodParamEndDate = periodParams.to * 1000;
    const now = new Date().getTime();

    if(periodParamEndDate > now) {
      periodParams.to = Math.trunc(now / 1000);
    }

    return periodParams;
  }

  getHistory(pair, periodParams, resolution) {
    const futureProofPeriodParams = this._getFutureProofPeriodParams(periodParams);
    const candlesHistory$ = this._getUnifiedCandlesHistory(pair, futureProofPeriodParams, resolution);

    candlesHistory$.subscribe(candles => {
      if (candles.length === 0) {
        return;
      }
      this._updateLastCandle(candles[candles.length - 1], `${pair}_#_${resolution}`);
    });

    return candlesHistory$;
  }

  private _updateLastCandle(latestCandle: TVBar, uuid: string) {
    const lastCandle = this._lastCandles.get(uuid);

    if (lastCandle && latestCandle.time < lastCandle.time) {
      return;
    }

    this._lastCandles.set(uuid, latestCandle);
  }

  private getCandlesRealtimeUpdates(pair, resolution, subscriberUID) {
    return this.wssCandleStickService.get(pair, this._getSecondsFromResolution(resolution)).pipe(
      map(candlesResponse => {
        const bars = this._getBarDataFromResponse(candlesResponse.data);
        const lastCandle = this._lastCandles.get(subscriberUID);
        const filteredBars = lastCandle ? bars.filter(newCandles => newCandles.time >= lastCandle.time) : bars;
        return filteredBars;
      }),
      filter(bars => {
        if (bars.length === 0) {
          return false;
        }

        return true;
      }),
      switchMap(bars => {
        this._updateLastCandle(bars[bars.length - 1], subscriberUID);
        return from(bars);
      })
    );
  }

  getRealtimeUpdates(pair: string, resolution: ResolutionString, subscriberUID: string, update: (bar: TVBar) => void, resetHistory: () => void) {
    if (this._realtimeSubscriptions.has(subscriberUID)) {
      return;
    }

    const candlesRealtime$ = this.getCandlesRealtimeUpdates(pair, resolution, subscriberUID);

    const subscription = candlesRealtime$.pipe(
      tap(bar => update(bar))
    ).subscribe();

    this._realtimeSubscriptions.set(subscriberUID, {
      pair,
      resolution,
      uuid: subscriberUID,
      update,
      resetHistory,
      subscription
    });
  }

  unsubscribeLiveUpdates(subscriberUID: string) {
    const currentRealtimeObject = this._realtimeSubscriptions.get(subscriberUID);

    if (!currentRealtimeObject) {
      return;
    }

    currentRealtimeObject.subscription?.unsubscribe();
    this._realtimeSubscriptions.delete(subscriberUID);
    this._lastCandles.delete(subscriberUID);
  }

  getCurrencyPrecision(currency: string) {
    return this.currenciesService.getCurrencyPrecision(currency)
  }
}
